import { ContentBox } from "@msuite/ui/content-box";
import { useFileUpload } from "@msuite/ui/file-upload";
import { resolver } from "@msuite/ui/libs/form";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { moment } from "@msuite/utils/date";
import { type ProtocolFormSchema, protocolFormSchema } from "@msuite/utils/schemas";
import { generateId } from "@msuite/utils/strings";
import type { Nullable, ProtocolType } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useUpload } from "@/api/files";
import { ProtocolForm } from "@/forms/protocol-form";
import { client } from "@/hono";

export function CreateProtocol() {
	/** Params */
	const params = new URLSearchParams(document.location.search);

	/** Hooks */
	const navigate = useNavigate();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({ uploadFn });
	const qc = useQueryClient();
	const methods = useForm({
		defaultValues: async () => {
			const isProjectContext = params.get("projectId") !== null;
			const base: Partial<ProtocolFormSchema> = {
				type: isProjectContext
					? "general"
					: (params.get("type") as Nullable<ProtocolType>) || undefined,
				date: moment().db(),
				content: [{ id: generateId() }],
			};
			if (params.get("projectId")) {
				base.subject = {
					type: "project",
					value: params.get("projectId")!,
				};
			}
			return base as ProtocolFormSchema;
		},
		resolver: resolver(protocolFormSchema),
		mode: "onChange",
	});

	async function handleOnSubmit(data: ProtocolFormSchema) {
		try {
			const uploadResults = await controller.handleOnFileUpload();
			await client.protocols.index.$post({
				json: {
					...data,
					uploadResults,
				},
			});
			methods.reset();
			controller.handleOnReset();
			await qc.invalidateQueries({ predicate: invalidate(["protocols"]) });
			navigate(-1);
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnReject(errors: typeof methods.formState.errors) {
		console.info(errors);
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<ContentBox bottomPadding="none">
				<form
					className="w-full h-full"
					onSubmit={methods.handleSubmit(handleOnSubmit, handleOnReject)}
					data-cy="protocol-form"
				>
					<ProtocolForm controller={controller} />
				</form>
			</ContentBox>
		</FormProvider>
	);
}
